import React, { useEffect } from 'react';
import { useDispatch } from 'react-redux';

import { BufferTracker } from '@bufferapp/buffer-tracking-browser-ts';
import { Text, Modal, Button } from '@bufferapp/ui';
import { blue, grayDarker, black } from '@bufferapp/ui/style/colors';
import { Cross, Checkmark } from '@bufferapp/ui/Icon';
import { SEGMENT_NAMES } from '~/constants';
import { actions as modalActions } from '~/modals/reducer';

import {
  HeadingText,
  TextStyled,
  CloseWrapper,
  BodyModal,
  TextWrapper,
  IconWrapper,
  Check,
  CTAWrapper,
} from './styles';

import { useAccount } from '../../../accountContext';

const onUpsellClick = ({
  dispatch,
}: {
  dispatch: (action: unknown) => void;
}): void => {
  const { MODALS, actions } = window?.appshell || {};
  actions.openModal(MODALS.planSelector, {
    cta: SEGMENT_NAMES.COMPOSER_REQUEST_APPROVAL_UPSELL,
    upgradePathName: 'approvals-upgrade',
  });

  dispatch(modalActions.hideTrialCollaborationModal());
};

const TrialCollaborationModal: React.FC = () => {
  const dispatch = useDispatch();
  const user = useAccount();
  const canStartTrial =
    user?.account?.currentOrganization?.billing?.canStartTrial;
  const headingText = canStartTrial
    ? 'Try our collaboration features for free!'
    : 'Try collaboration features with the Team Pack';

  const commonTrackingProps =
    user?.account?.currentOrganization?.commonTrackingProperties || null;

  useEffect(() => {
    const { COMPONENTS, actions } = window?.appshell || {};
    if (canStartTrial) {
      actions.renderComponent({
        containerId: 'collab-start-trial-cta',
        componentKey: COMPONENTS.startTrialButton,
        options: {
          cta: 'startTrial',
          ctaButton: 'draft-startTrial-1',
          upgradePathName: 'approvals-trial',
        },
      });
    }
  }, [canStartTrial]);

  useEffect(() => {
    BufferTracker.pageViewed({
      name: 'Draft Paywall Modal',
      product: 'publish',
      cta: 'Drafts Request Approval',
    });

    BufferTracker.ctaViewed({
      organizationId: user?.account?.currentOrganization?.id || null,
      ctaName: 'publish-composer-collaborationModal-discoverTheTeamPack-1',
      currentChannelQuantity:
        user?.account?.currentOrganization?.channels?.length || 0,
      upgradePathName: 'approvals-upgrade',
      product: 'publish',
      ...commonTrackingProps,
    });
  }, [user, commonTrackingProps]);

  return (
    <Modal action={{}} width="wide" dismissible={false}>
      <CloseWrapper>
        <IconWrapper>
          <Cross
            onClick={() => dispatch(modalActions.hideTrialCollaborationModal())}
          />
        </IconWrapper>
      </CloseWrapper>

      <BodyModal background="url('https://buffer-publish.s3.amazonaws.com/images/celebrate%402x.svg') no-repeat">
        <TextWrapper>
          <HeadingText type="h1" color={black}>
            {headingText}
          </HeadingText>
          <Text type="p" color={grayDarker}>
            Send drafts for approval or review other&apos;s drafts plus...
          </Text>
          <Check>
            <Checkmark color={blue} />
            <TextStyled type="p" color={grayDarker}>
              Unlocked publishing, analytics and engagement
            </TextStyled>
          </Check>
          <Check>
            <Checkmark color={blue} />
            <TextStyled type="p" color={grayDarker}>
              Unlimited seats for your team
            </TextStyled>
          </Check>
          <Check>
            <Checkmark color={blue} />
            <TextStyled type="p" color={grayDarker}>
              Easy, clear exportable reports
            </TextStyled>
          </Check>
        </TextWrapper>
        {canStartTrial && (
          <CTAWrapper>
            <div id="collab-start-trial-cta" />
            <Button
              type="secondary"
              label="See Paid Plans"
              onClick={() => {
                onUpsellClick({ dispatch });
              }}
            />
          </CTAWrapper>
        )}
        {!canStartTrial && (
          <Button
            type="primary"
            label="Discover the Team Pack"
            onClick={() => {
              BufferTracker.ctaClicked({
                organizationId: user?.account?.currentOrganization?.id || '',
                clientName: 'publishWeb',
                product: 'publish',
                cta: 'Drafts Request Approval',
                ctaName: 'publish-composer-publish-draftRequestApproval-1',
                upgradePathName: 'approvals-upgrade',
                ...commonTrackingProps,
              });
              onUpsellClick({ dispatch });
            }}
          />
        )}
      </BodyModal>
    </Modal>
  );
};

export default TrialCollaborationModal;
